import request from '@/utils/request';
import { loadEthers } from '@/utils/load-ethers';
import { getOrCreateDeviceId } from '@/utils/device-id';
import { generateBotProof } from '@/utils/botDetector';

/** 登录/验签等公开接口：不附带旧 JWT，401 时不触发全局登出 */
export const AUTH_PUBLIC_REQUEST_CONFIG = {
  _skipAuthHeader: true,
  _skipAuthRefresh: true,
};

async function normalizeWalletAddress(raw) {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  if (!/^0x[0-9a-fA-F]{40}$/.test(value)) return value;
  try {
    const { getAddress } = await loadEthers();
    return getAddress(value);
  } catch {
    return value;
  }
}

/** POST 发送邮箱验证码 */
export async function sendEmailVerificationCode(email, captchaToken) {
  const botProof = await generateBotProof('auth_email_code');
  return request.post(
    '/liyuan-auth/email/code',
    {
      email,
      captchaToken: captchaToken || undefined,
      deviceId: getOrCreateDeviceId(),
      botProof: botProof || undefined,
    },
    AUTH_PUBLIC_REQUEST_CONFIG
  );
}

/**
 * POST 绑定邮箱（需已登录）
 * POST /liyuan-auth/bind/email
 */
export function bindEmail(payload) {
  return request.post('/liyuan-auth/bind/email', {
    loginType: 'EMAIL',
    email: payload.email,
    code: payload.code,
  });
}

const ORIGIN_LOGIN_TYPES = new Set(['EMAIL', 'GOOGLE', 'WALLET']);

function normalizeOriginLoginType(value, fallback = 'WALLET') {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase();
  if (ORIGIN_LOGIN_TYPES.has(normalized)) return normalized;
  // 显式传空串时保留空（供 bindPrivyAccount 拦截判断），勿强行落成 WALLET
  if (value !== undefined && value !== null && String(value).trim() === '') {
    return '';
  }
  return fallback;
}

/**
 * 登录注册用 BotProof：PoW 失败必须上抛，禁止吞错后空票请求（PROD bot_proof_required 下会 816）。
 * @param {{ botProof?: string, event?: Event, pow?: object }} payload
 */
async function resolveLoginBotProof(payload) {
  if (payload?.botProof) return payload.botProof;
  // payload.pow：钱包路径在唤起签名前预取的 PoW，避免签名返回后 WebView 网络抖动导致取题失败
  return generateBotProof('auth_login', payload?.event, payload?.pow || null);
}

/** POST 邮箱验证码登录 */
export async function loginWithEmail(payload) {
  const botProof = await resolveLoginBotProof(payload);
  return request.post(
    '/liyuan-auth/login/email',
    {
      loginType: 'EMAIL',
      originLoginType: normalizeOriginLoginType(payload.originLoginType, 'EMAIL'),
      email: payload.email,
      code: payload.code,
      inviteCode: payload.inviteCode || undefined,
      deviceId: getOrCreateDeviceId(),
      captchaToken: payload.captchaToken || undefined,
      botProof: botProof || undefined,
    },
    AUTH_PUBLIC_REQUEST_CONFIG
  );
}

/** POST Google 登录（googleToken 为 Google ID Token） */
export async function loginWithGoogle(payload) {
  const botProof = await resolveLoginBotProof(payload);
  return request.post(
    '/liyuan-auth/login/google',
    {
      loginType: 'GOOGLE',
      originLoginType: normalizeOriginLoginType(payload.originLoginType, 'GOOGLE'),
      googleToken: payload.googleToken,
      inviteCode: payload.inviteCode || undefined,
      deviceId: getOrCreateDeviceId(),
      captchaToken: payload.captchaToken || undefined,
      // 须在 Google OAuth 前预取；新注册校验 ≥3s 生理窗（存量登录后端忽略）
      serverTicket: payload.serverTicket || undefined,
      botProof: botProof || undefined,
    },
    AUTH_PUBLIC_REQUEST_CONFIG
  );
}

/**
 * Privy 嵌入式钱包验签 → /login/wallet（addressType=2）。
 * 邮箱/Google 主路径禁止调用：应在平台 /login/email|/login/google Session 下直接 trade-address/add type=2。
 * 保留供遗留/调试路径；生产邮箱登录走 privy-platform-login → finalizeCustodialTradeAddressBind。
 */
export async function bindPrivyAccount(payload) {
  const originLoginType = normalizeOriginLoginType(payload.originLoginType, '');
  if (originLoginType === 'EMAIL' || originLoginType === 'GOOGLE') {
    return Promise.reject(
      new Error(
        'EMAIL/GOOGLE must bind via trade-address/add under platform session; do not call login/wallet'
      )
    );
  }

  const walletAddress = String(payload.walletAddress ?? payload.address ?? '').trim();
  const signature = String(payload.signature ?? '').trim();
  const message = String(payload.message ?? '').trim();
  const privyWalletId = String(payload.privyWalletId ?? payload.privyUserId ?? '').trim();

  if (!privyWalletId) {
    throw new Error('privyWalletId is required');
  }

  return loginWithWallet({
    walletAddress,
    signature,
    message,
    addressType: 2,
    privyWalletId,
    inviteCode: payload.inviteCode || undefined,
    originLoginType: originLoginType || 'WALLET',
  });
}

/** POST 钱包验签登录（外部钱包 addressType=1；遗留 Privy 路径 addressType=2） */
export async function loginWithWallet(payload) {
  const walletAddress = await normalizeWalletAddress(payload.walletAddress);
  const signature = String(payload.signature ?? '').trim();
  const message = String(payload.message ?? '').trim();
  const addressType = Number(payload.addressType ?? 1);
  const privyWalletId = String(payload.privyWalletId ?? '').trim();
  const originLoginType = normalizeOriginLoginType(payload.originLoginType, 'WALLET');

  // 红线：邮箱/Google 再走 login/wallet 会以嵌入式地址新建 WALLET 孤儿账户
  if (originLoginType === 'EMAIL' || originLoginType === 'GOOGLE') {
    return Promise.reject(
      new Error(
        'EMAIL/GOOGLE must use /login/email or /login/google then trade-address/add; login/wallet creates orphan accounts'
      )
    );
  }

  if (!walletAddress || !signature || !message) {
    return Promise.reject(new Error('wallet login fields required'));
  }
  if (addressType !== 1 && addressType !== 2) {
    return Promise.reject(new Error('addressType must be 1 or 2'));
  }
  if (addressType === 2 && !privyWalletId) {
    return Promise.reject(new Error('privyWalletId is required'));
  }

  const botProof = await resolveLoginBotProof(payload);

  const body = {
    loginType: 'WALLET',
    originLoginType,
    walletAddress,
    signature,
    message,
    addressType,
    inviteCode: payload.inviteCode || undefined,
    deviceId: getOrCreateDeviceId(),
    captchaToken: payload.captchaToken || undefined,
    botProof: botProof || undefined,
  };
  if (addressType === 2) {
    body.privyWalletId = privyWalletId;
  }

  return request.post('/liyuan-auth/login/wallet', body, AUTH_PUBLIC_REQUEST_CONFIG);
}

/** DELETE 退出登录 */
export function logoutApi() {
  return request.delete('/liyuan-auth/logout');
}

/** POST 刷新 Token */
export function refreshTokenApi(refreshToken) {
  return request.post('/liyuan-auth/refresh', { refreshToken }, AUTH_PUBLIC_REQUEST_CONFIG);
}
